import type { ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EmptyStateTone = 'neutral' | 'warning' | 'danger' | 'info';

const TONES: Record<EmptyStateTone, string> = {
  neutral: 'bg-surface-sunken text-fg-subtle',
  warning: 'bg-warning-soft text-warning-text',
  danger: 'bg-danger-soft text-danger-text',
  info: 'bg-info-soft text-info-text',
};

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  tone?: EmptyStateTone;
  size?: 'sm' | 'md';
  className?: string;
}

/** Пустое состояние: нет данных, нет прав, ничего не найдено по фильтрам. */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  actions,
  tone = 'neutral',
  size = 'md',
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-full',
          size === 'sm' ? 'size-9' : 'size-12',
          TONES[tone],
        )}
      >
        <Icon className={size === 'sm' ? 'size-4' : 'size-5'} aria-hidden="true" />
      </span>
      <div className="max-w-md space-y-1">
        <p className={cn('font-semibold text-fg', size === 'sm' ? 'text-sm' : 'text-base')}>{title}</p>
        {description ? <p className="text-sm leading-relaxed text-fg-muted">{description}</p> : null}
      </div>
      {actions ? <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{actions}</div> : null}
    </div>
  );
}
